"use client";

import "@/components/home/the-problem.css";
import { Button } from "@/components/ui/shadcn/button";
import { heroSerif } from "@/lib/theme/hero-serif";

import { memo, useState } from "react";

type Story = {
    label: string;
    headline: string;
    paras: string[];
    closer: string;
};

// The section's resting story — the original op-ed, verbatim.
const PROBLEM: Story = {
    label: "The Problem",
    headline: "You bought a software, but it hired you.",
    paras: [
        "Your platform decides more about your business than you'd think. It decides how fast you can bring on clients, how good your disputes actually are, and how many clients you can handle before you're forced to hire. If the software is limited, your business is limited right along with it.",
        "The software we've all had access to for years is incredibly manual and outdated. Everything is click after click, screen after screen, doing by hand what the software should be doing for you. The cheap tools leave the hard work to you, and the expensive ones charge more without fixing any of it.",
        "Either way, you end up doing jobs the software should be doing. Sitting through tutorials, hiring people just to work around it, pasting client data into ChatGPT because your CRM can't answer a basic question about your own clients.",
        "And here's the part that bothers us the most. When a round comes back, every platform in this industry does the exact same thing: it rewords round one and sends it again. A new date, a shuffled template, the same letter the bureaus have already read a million times. That's not a new round, that's a rerun.",
        "Fluid doesn't reword anything. It builds the next round from scratch, using your client's actual situation and what got deleted in every round before it. Round two hits harder because it genuinely knows more.",
    ],
    closer: "You started this business to fix people's credit, not to be a power user of someone else's software.",
};

const INDEX: Story[] = [
    {
        label: "Fully automated",
        headline: "The whole cycle, hands off.",
        paras: [
            "Here's what actually happens when you stop doing the work. Fluid logs into monitoring, pulls fresh reports, and reads all three bureaus before you've opened your laptop. It picks what to dispute, writes the letters, and mails them with tracking.",
            "Every round preps itself and stops at exactly one gate: your approval. You read what it built, you hit approve, and it ships. Nothing goes out without you. That's a rule, not a setting.",
            "And when results come back, you don't chase them. Outcomes are read automatically, deletions get recorded, and the next round builds itself off what actually happened. You'll spend more time deciding and almost none doing.",
        ],
        closer: "Your job shrinks to the one decision that matters.",
    },
    {
        label: "Scheduled imports",
        headline: "Reports pull themselves.",
        paras: [
            "You know the routine. Log into the monitoring portal, wait for it to load, download the report, do it again for the next client. Now multiply that by your whole book, every month.",
            "Enroll a client once and that routine is over. Fresh reports land on cadence, or the moment a round needs one. Credentials sit in an encrypted vault, and if a login throws a one-time code, the import pauses and picks up right where it left off.",
            "Every import gets compared against the last one. New items get spotted, deletions get caught and credited to the round that earned them, and anything that sneaks back after a deletion gets a formal reinsertion notice instead of a shrug.",
        ],
        closer: "You stop logging into monitoring portals. Forever.",
    },
    {
        label: "Scheduled disputes",
        headline: "Rounds on a calendar.",
        paras: [
            "Set a cadence per client and rounds prep themselves, or fire the moment a fresh report lands. Items selected, addresses resolved, letters drafted, and the whole thing queued up waiting on your review.",
            "The FCRA clock runs per round, so you never have to remember who's at day 28. If a bureau goes silent past the deadline, the next letter demands deletion. If they claim verified without showing proof, the next letter demands to see how. Round two is never round one reworded.",
            "Your book stops depending on your memory. Whether you're running ten clients or four hundred, every one of them is somewhere in a round, and the system knows exactly where.",
        ],
        closer: "The pipeline moves whether you're watching or not.",
    },
    {
        label: "Automated communications",
        headline: "Clients hear from you constantly. You type nothing.",
        paras: [
            "Most of what your clients want to know is the same three questions. Where are we, what happened, and what's next. You shouldn't be answering those by hand, one text at a time.",
            "Drip campaigns send from your own domain. Score alerts, round updates, and document requests go out on their own, over email and SMS, in your name. Every message reads like you wrote it, because you did, once.",
            "And the portal answers the “any update?” call before it happens. Clients watch their disputes move in plain language, round by round, without picking up the phone.",
        ],
        closer: "Fewer status calls. Fewer chargebacks.",
    },
    {
        label: "Client portal",
        headline: "Your clients get a front door.",
        paras: [
            "Your clients sign in at your domain, see your logo, and watch their scores climb. They read their dispute progress in plain language, upload documents when asked, and pay their invoices, all in one place.",
            "Onboarding runs itself too. Agreements, e-sign, ID upload, monitoring credentials. You send one link and a stranger becomes a client with a first round in motion, without you touching a thing.",
            "That transparency does real work for you. A client who can see what's happening doesn't call for status, doesn't wonder what they're paying for, and doesn't charge back.",
        ],
        closer: "It looks like you built it. That's the point.",
    },
    {
        label: "White label",
        headline: "Runs under your logo.",
        paras: [
            "Your logo, your colors, your domain. The dashboard, the client portal, and onboarding all carry your name. Custom domains provision automatically, verified by a DNS wizard in minutes, not support tickets.",
            "Your clients will never see our name. Not on the login page, not in an email footer, not in the portal. As far as they know, you built this.",
            "Other platforms charge five to ten thousand dollars for that privilege. We think it's just what the software should do, so it's included.",
        ],
        closer: "Nobody ever has to know we exist.",
    },
];

export const TheProblem = memo(function TheProblem() {
    // `selected` drives the index buttons; `story` is the rendered copy.
    const [selected, setSelected] = useState<number | null>(null);
    const [story, setStory] = useState<number | null>(null);

    const pickFeature = (index: number) => {
        const next = selected === index ? null : index;
        setSelected(next);
        setStory(next);
    };

    const current = story === null ? PROBLEM : INDEX[story]!;

    return (
        <section className={`dw-home__section ${heroSerif.variable}`} aria-label="The problem" data-component="TheProblem" data-location="src/components/home/the-problem.tsx">
            <div className="dw-home__measure dw-home__stack">
                <div className="dw-home-problem__masthead">
                    <ul className="dw-home-problem__index dw-text-caption dw-tabular-nums" aria-label="Platform features">
                        {INDEX.map((item, index) => (
                            <li key={item.label}>
                                <Button variant="bare" size="auto"
                                    type="button"
                                     data-tone={selected === index ? "foreground" : "muted"}
                                    aria-pressed={selected === index}
                                    onClick={() => pickFeature(index)}
                                >
                                    {item.label}
                                </Button>
                            </li>
                        ))}
                    </ul>
                    <div className="dw-home__kicker-row dw-text-caption dw-tabular-nums" data-tone="foreground">
                        <span>01 · {current.label}</span>
                        <span data-tone="muted">DisputeWell, 2026</span>
                    </div>
                </div>
                <div key={story ?? "problem"} className="dw-home-problem__story" aria-live="polite">
                    <h2 className="dw-home-problem__headline dw-text-hero-lg" data-tone="foreground">
                        {current.headline}
                    </h2>
                    <div className="dw-home-problem__columns">
                        {current.paras.map((para, index) => (
                            <p key={para.slice(0, 24)} className="dw-home-problem__para dw-text-body" data-cap={index === 0 ? "" : undefined} data-tone="foreground">
                                {para}
                            </p>
                        ))}
                        <p className="dw-home-problem__para dw-text-lead" data-kicker="" data-tone="foreground">
                            {current.closer}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
});
